import { useCallback, useEffect, useState } from 'react';

function readParams(): URLSearchParams {
  const hash = window.location.hash || '#/';
  const [, query = ''] = hash.split('?');
  return new URLSearchParams(query);
}

function writeParams(params: URLSearchParams) {
  const hash = window.location.hash || '#/';
  const [path] = hash.split('?');
  const query = params.toString();
  window.history.replaceState(null, '', query ? `${path}?${query}` : path);
}

export function useHashFilters<T extends Record<string, unknown>>(defaults: T) {
  const [filters, setFilters] = useState<T>(() => {
    const params = readParams();
    const next = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const value = params.get(key);
      if (value != null) next[key as keyof T] = value as T[keyof T];
    }
    return next;
  });

  useEffect(() => {
    const params = readParams();
    for (const [key, value] of Object.entries(filters)) {
      const normalized = value == null ? '' : String(value);
      if (!normalized || normalized === 'all') params.delete(key);
      else params.set(key, normalized);
    }
    writeParams(params);
  }, [filters]);

  const setFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  return { filters, setFilters, setFilter };
}
